import { VolumeIcon } from '@/components/media/volume-icon';
import { MediaControls } from '@/hooks/use-media-controls/types';
import { useSmoothedTime } from '@/hooks/use-smoothed-time';
// eslint-disable-next-line no-restricted-imports -- raw token read kept until this site is moved to theme.semantics
import { DS_COLOR } from '@calm/evexia-design-tokens';
import { SECOND_MS } from '@calm/web-common/dist/time';
import { Replay } from '@mui/icons-material';
import playerMessages from '@rhg/lang/messages/player';
import {
    Box,
    Fade,
    IconButton,
    Slider,
    SxProps,
    alpha,
    useTheme,
    useMediaQuery,
    Theme,
} from '@rhg/libs/material';
import { PauseCtaFilled, PlayCtaFilled } from '@rhg/libs/material/icons';
import { sxAppend } from '@rhg/libs/material/sx';
import { displayTime } from '@rhg/utils/media';
import { ReactElement, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { MediaSubtitles } from '../../subtitles';
import { ControlButtonType } from '../types';

const GRADIENT_OVERLAY_ASPECT_RATIO = 229 / 1440;
const FADE_IN_ANIMATION_DURATION = 500;

export interface VideoMinimalControlsProps {
    controls: MediaControls;
    sx?: SxProps;
    actionButton?: ReactElement;
    onControlClick?: (buttonType: ControlButtonType) => void;
    areControlsHidden?: boolean;
}

const styleVideoControlsOverlay = {
    position: 'absolute',
    width: '100%',
    height: '100%',
    top: 0,
    left: 0,
};

const styleBottomContainer = {
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: '100%',
};

const styleBottomSubtitles = { mb: 2 };

const styleActionFade = { width: '100%' };

const styleControlsContainer = {
    px: 8,
};

const styleControlsAllButtonsContainer: SxProps = {
    width: '100%',
    display: 'flex',
    gap: 4,
    justifyContent: 'flex-end',
    py: 6,
};

const styleControlsCoreButtonsContainer: SxProps<Theme> = {
    width: {
        xs: '100%',
        sm: '100%',
        md: 'auto',
    },
    display: 'flex',
    gap: 4,
    justifyContent: 'flex-end',
    p: (theme: Theme) => ({
        xs: theme.ds.spacingUnits.SIDE_SMALL,
        sm: theme.ds.spacingUnits.SIDE_SMALL,
        md: 0,
    }),
    // Adding a zIndex to prevent overlap with header
    position: 'relative',
    zIndex: 2,
};

const styleTimingControl = {
    py: 1,
    mb: 3.25,
    height: 4,
    width: '100%',
    // MUI's own SliderThumb/SliderTrack bake in a `left/width/bottom/height`
    // transition unconditionally. useSmoothedTime repositions the thumb every
    // animation frame (~60-100Hz) — far faster than that transition can
    // settle — so each frame restarts it before it paints, freezing the
    // thumb in place. Disable it; JS now owns the motion. EVE-13651.
    '& .MuiSlider-thumb': {
        transition: 'none',
    },
    '& .MuiSlider-track': {
        transition: 'none',
    },
};

// Dark translucent fill behind the icon so play/pause/mute stay legible
// against bright video frames. Sizing comes from IconButton `size="large"`;
// this style only contributes the backdrop color + hover state.
// Note: the IconButton `contained` variant is similar in concept but is
// mode-aware (renders `background.paper` in light mode), which would
// produce a light disc over the video — unsuitable here.
const styleBackdrop = {
    background: DS_COLOR.BLACK_60P,
    '&:hover': {
        backgroundColor: {
            xs: 'mask.black.60p',
            md: 'mask.white.8p',
        },
    },
};

const styleGradientOverlay = {
    position: 'absolute',
    bottom: 0,
    width: '100%',
    height: `calc(${GRADIENT_OVERLAY_ASPECT_RATIO} * 100dvw)`,
    background: `linear-gradient(180deg, ${alpha(DS_COLOR.WHITE_100P, 0)} 0%, ${
        DS_COLOR.BLACK_100P
    } 100%)`,
};

export function VideoMinimalControls({
    controls: videoControls,
    sx,
    actionButton,
    onControlClick,
    areControlsHidden = false,
}: VideoMinimalControlsProps) {
    const { formatMessage } = useIntl();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const {
        currentCue,
        isMuted,
        timingState,
        isPaused,
        togglePlay,
        toggleMuted,
        setTime,
        playbackRate,
    } = videoControls;
    const areControlsVisible = !areControlsHidden;
    const areSubtitlesUnderScrubber = areControlsHidden && isMobile;

    // Interpolates between the ~4Hz ticks so the thumb moves smoothly, while
    // still snapping instantly on seek/track-change. EVE-13651.
    const smoothedCurrentTime = useSmoothedTime({
        currentTime: timingState.currentTime,
        isPaused: timingState.isPaused,
        playbackRate,
        duration: timingState.duration,
    });

    // Analytics uses `controls.isPaused` (DOM truth); UI uses
    // `timingState.isPaused` (React state, needed to trigger renders).
    // They can drift one render after a media event — unify only by
    // refactoring `useMediaControls`.
    const handleTogglePlay = useCallback(() => {
        if (onControlClick) {
            // The reverse of the current state, prior to calling togglePlay
            onControlClick(isPaused ? 'play' : 'pause');
        }

        togglePlay();
    }, [isPaused, onControlClick, togglePlay]);

    const controls = useMemo(() => {
        return (
            <Box sx={styleControlsCoreButtonsContainer}>
                <Fade
                    in={areControlsVisible}
                    timeout={FADE_IN_ANIMATION_DURATION}
                >
                    <IconButton
                        trackButton={
                            timingState.isPaused
                                ? 'VideoPlayerPlay'
                                : 'VideoPlayerPause'
                        }
                        onClick={handleTogglePlay}
                        aria-label={
                            timingState.isPaused
                                ? timingState.currentTime > 0 &&
                                  timingState.remainingTime === 0
                                    ? formatMessage(
                                          playerMessages['player.replay']
                                      )
                                    : formatMessage(
                                          playerMessages['player.play']
                                      )
                                : formatMessage(playerMessages['player.pause'])
                        }
                        size="medium"
                        sx={styleBackdrop}
                    >
                        {timingState.isPaused ? (
                            timingState.currentTime > 0 &&
                            timingState.remainingTime === 0 ? (
                                <Replay />
                            ) : (
                                <PlayCtaFilled />
                            )
                        ) : (
                            <PauseCtaFilled />
                        )}
                    </IconButton>
                </Fade>
                <Fade
                    in={areControlsVisible}
                    timeout={FADE_IN_ANIMATION_DURATION}
                >
                    <IconButton
                        trackButton={
                            isMuted ? 'VideoPlayerUnmute' : 'VideoPlayerMute'
                        }
                        onClick={() => toggleMuted()}
                        size="medium"
                        sx={styleBackdrop}
                        aria-label={
                            isMuted
                                ? formatMessage(playerMessages['player.unmute'])
                                : formatMessage(playerMessages['player.mute'])
                        }
                    >
                        <VolumeIcon isMuted={isMuted} />
                    </IconButton>
                </Fade>
            </Box>
        );
    }, [
        areControlsVisible,
        timingState.isPaused,
        timingState.currentTime,
        timingState.remainingTime,
        handleTogglePlay,
        formatMessage,
        isMuted,
        toggleMuted,
    ]);

    return (
        <Box sx={sxAppend(styleVideoControlsOverlay, sx)}>
            {isMobile && controls}
            <Fade in={areControlsVisible} timeout={FADE_IN_ANIMATION_DURATION}>
                <Box sx={styleGradientOverlay} />
            </Fade>
            <Box sx={styleBottomContainer}>
                {!areSubtitlesUnderScrubber && (
                    <MediaSubtitles cue={currentCue} isMuted={isMuted} />
                )}
                <Box sx={styleControlsContainer}>
                    <Box sx={styleControlsAllButtonsContainer}>
                        {!!actionButton && (
                            <Fade
                                style={isMobile ? styleActionFade : undefined}
                                in={areControlsVisible}
                                timeout={FADE_IN_ANIMATION_DURATION}
                            >
                                <Box>{actionButton}</Box>
                            </Fade>
                        )}
                        {!isMobile && controls}
                    </Box>
                    <Fade
                        in={areControlsVisible}
                        timeout={FADE_IN_ANIMATION_DURATION}
                    >
                        <Slider
                            sx={styleTimingControl}
                            value={smoothedCurrentTime}
                            max={timingState.duration}
                            aria-label={formatMessage(
                                playerMessages['player.seek_slider']
                            )}
                            getAriaValueText={(sliderValue) =>
                                formatMessage(
                                    playerMessages['player.current_time'],
                                    {
                                        time: displayTime(sliderValue),
                                    }
                                )
                            }
                            step={timingState.duration / SECOND_MS}
                            size="small"
                            onChangeCommitted={(_, newTime) => {
                                setTime(newTime as number);
                            }}
                        />
                    </Fade>
                    {areSubtitlesUnderScrubber && (
                        <MediaSubtitles
                            cue={currentCue}
                            isMuted={isMuted}
                            sx={styleBottomSubtitles}
                        />
                    )}
                </Box>
            </Box>
        </Box>
    );
}
